import { useState, useCallback, useEffect } from "react";
import {
  Form,
  Button,
  Select,
  Input,
  Table,
  Modal,
  AutoComplete,
  message,
  DatePicker,
  Upload,
  Image,
  List,
  Card,
} from "antd";
import {
  UserAddOutlined,
  PlusOutlined,
  DownloadOutlined,
  PlusCircleFilled,
  ClockCircleOutlined,
  ReloadOutlined,
  SaveFilled,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import moment from "moment";

import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";
import FormButtonsWrap from "../../component/form-buttons-wrap/form-buttons-wrap.component";
import ScheduleFrom from "../../component/schedule-form/schedule-form.component";
import PreAuthorizedAPI from "../../api/PreAuthorizedAPI";
import ResidentAPI from "../../api/ResidentAPI";
import SchedulesGroupAPI from "../../api/SchedulesGroupAPI";
import SchedulesAPI from "../../api/SchedulesAPI";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import { setMenu } from "../../redux/reducers/navigationSlice";
import { sortByDate, sortAlphabetically } from "../../utils/tableUtils";
import {
  formatAsDOPhone,
  weekDaysNumbersToESText,
} from "../../utils/dataUtils";

import "./pre-authorized-maintenance.styles.css";

const statusMap = {
  PENDING: "Pendiente",
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
};

const initialFormValues = {
  id: "",
  documentId: "",
  name: "",
  lastName: "",
  gender: "",
  residentId: "",
  residentName: "",
  phone: "",
  scheduleGroupId: "",
  expirationDate: null,
  status: "PENDING",
  picture: "",
};

const initialFindFormValues = {
  documentId: "",
  fullName: "",
  gender: "",
  residentId: "",
  scheduleGroupId: "",
  createdBy: "",
  status: "",
};

const PreAuthorizedMaintenancePage = () => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userAccount.user.token);

  const [preAuthorizedData, setPreAuthorizedData] = useState({
    preAuthorized: [],
    totalCount: 0,
  });
  const [formModalIsOpen, setFormModalIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [isSelected, setIsSelected] = useState(false);
  const [residents, setResidents] = useState([]);
  const [scheduleGroups, setScheduleGroups] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [picture, setPicture] = useState("");
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [selectedPreAuthorizedId, setSelectedPreAuthorizedId] = useState(null);
  const [scheduleModalIsOpen, setScheduleModalIsOpen] = useState(false);

  const listColumns = [
    {
      title: "Id",
      dataIndex: "Id",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.Id - b.Id,
    },
    {
      title: "Documento",
      dataIndex: "DocumentId",
      sorter: (a, b) => sortAlphabetically(a, b, "DocumentId"),
    },
    {
      title: "Nombre Completo",
      dataIndex: "FullName",
      sorter: (a, b) => sortAlphabetically(a, b, "FullName"),
    },
    {
      title: "Género",
      dataIndex: "Gender",
      width: 70,
    },
    {
      title: "Teléfono",
      dataIndex: "Phone",
    },
    {
      title: "Residente",
      dataIndex: "ResidentName",
    },
    {
      title: "Grupo Horario",
      dataIndex: "ScheduleGroupName",
    },
    {
      title: "Fecha Expiración",
      dataIndex: "ExpirationDate",
      sorter: (a, b) => sortByDate(a, b, "ExpirationDate"),
    },
    {
      title: "Creado",
      dataIndex: "CreatedDate",
      sorter: (a, b) => sortByDate(a, b, "CreatedDate"),
    },
    {
      title: "Actualizado",
      dataIndex: "UpdatedDate",
      sorter: (a, b) => sortByDate(a, b, "UpdatedDate"),
    },
    {
      title: "Creado Por",
      dataIndex: "CreatedByName",
    },
    {
      title: "Estado",
      dataIndex: "Status",
      render: (_, { Status }) => <>{statusMap[Status]}</>,
    },
    {
      title: "Opción",
      dataIndex: "view",
      render: (_, { Id }) => (
        <>
          <Button
            onClick={() => {
              loadSelectedPreAuthorized(Id);
              setIsSelected(true);
            }}
          >
            Seleccionar
          </Button>
        </>
      ),
    },
  ];

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  };

  const onChangeResidentName = async (name) => {
    try {
      const residentsResponse = await ResidentAPI.getResidentsByName(
        token,
        name
      );

      setResidents(
        residentsResponse.data.residents.map((resident) => ({
          label: resident.Name,
          value: resident.Id,
        }))
      );
    } catch (ex) {
      let message = "No se pudo cargar residentes. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  };

  const onSelectResidentName = async (id, currentForm) => {
    currentForm.setFieldValue("residentId", id);
    const selectedLabel = residents.find(
      (resident) => resident.value === id
    ).label;
    currentForm.setFieldValue("residentName", selectedLabel);
  };

  const loadInitialData = useCallback(async () => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await PreAuthorizedAPI.getPreAuthorized(token);
      const scheduleGroupsResponse = await SchedulesGroupAPI.getSchedulesGroup(
        token
      );

      if (!response.data.success || !scheduleGroupsResponse.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setPreAuthorizedData(response.data);
      setScheduleGroups(scheduleGroupsResponse.data.schedulesGroup);
    } catch (ex) {
      console.error(ex.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    } finally {
      messageApi.destroy();
    }
  }, [messageApi, dispatch, token]);

  const loadPreAuthorizedList = async (activePage = 1, activePageSize = 10) => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await PreAuthorizedAPI.getPreAuthorized(token, {
        page: activePage,
        pageSize: activePageSize,
        ...searchForm.getFieldsValue(),
      });

      setCurrentPage(activePage);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setPreAuthorizedData(response.data);
    } catch (ex) {
      console.error(ex.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    } finally {
      messageApi.destroy();
    }
  };

  useEffect(() => {
    loadInitialData();
    dispatch(setMenu("preAuthorizedMaintenance"));
  }, [loadInitialData, dispatch]);

  const onFinishFormPreAuthorized = async (values) => {
    if (
      values.documentId === "" ||
      values.name === "" ||
      values.lastName === "" ||
      values.gender === "" ||
      values.residentId === "" ||
      values.status === ""
    ) {
      messageApi.open({
        type: "warning",
        content: "Favor completar formulario.",
      });
      return;
    }

    if (selectedSchedules.length < 1 && !values.scheduleGroupId) {
      messageApi.open({
        type: "warning",
        content:
          "Debe seleccionar un grupo de horario o al menos un rango de horario.",
      });
      return;
    }

    if (values.expirationDate) {
      values.expirationDate = dayjs(values.expirationDate).format(
        "YYYY-MM-DD HH:mm:ss"
      );
    }

    if (picture) {
      values.picture = picture;
    }

    try {
      let response = null;

      if (isSelected) {
        response = await PreAuthorizedAPI.updatePreAuthorized(token, {
          ...values,
          selectedSchedules,
        });
      } else {
        response = await PreAuthorizedAPI.createPreAuthorized(token, {
          ...values,
          selectedSchedules,
        });
      }

      messageApi.open({
        type: response.data.success ? "success" : "error",
        content: response.data.message,
      });

      if (response.data.success) {
        setFormModalIsOpen(false);
        loadPreAuthorizedList();
      }

      resetModalForm();
    } catch (ex) {
      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  };

  const onFinishScheduleForm = async (values) => {
    const { id, description, weekDays } = values;
    const startTime = dayjs(values.startTime).format("HH:mm:ss");
    const endTime = dayjs(values.endTime).format("HH:mm:ss");

    if (weekDays.length < 1) {
      messageApi.open({
        type: "warning",
        content: "Favor elegir al menos un día.",
      });
      return;
    }

    const weekDaysValues = weekDays.join(",");

    try {
      let response = null;

      if (!id || isNaN(id)) {
        response = await SchedulesAPI.createSchedule(token, {
          description,
          startTime,
          endTime,
          weekDays: weekDaysValues,
          preAuthorizedId: selectedPreAuthorizedId,
        });
      } else {
        response = await SchedulesAPI.updateSchedule(token, {
          id,
          description,
          startTime,
          endTime,
          weekDays: weekDaysValues,
          preAuthorizedId: selectedPreAuthorizedId,
        });
      }

      const responseBody = response.data;

      messageApi.open({
        type: responseBody.success ? "success" : "error",
        content: responseBody.message,
      });
      addSelectedSchedule({
        Id: id,
        Description: description,
        WeekDays: weekDaysNumbersToESText(weekDaysValues, true),
        TimeRange: `${startTime} a ${endTime}`,
      });
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const loadSelectedPreAuthorized = async (id) => {
    try {
      const response = await PreAuthorizedAPI.getPreAuthorizedById(token, id);
      const schedulesResponse = await SchedulesAPI.getSchedulesByPreAuthorized(
        token,
        id
      );
      const schedulesData = schedulesResponse.data;

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      if (!schedulesData.success) {
        messageApi.destroy();
        messageApi.open({
          type: "warning",
          content: schedulesData.message,
        });
        return;
      }

      const {
        Id,
        DocumentId,
        Name,
        LastName,
        Gender,
        ResidentId,
        ResidentName,
        Phone,
        ScheduleGroupId,
        ExpirationDate,
        Status,
        Picture,
      } = response.data.preAuthorized;

      setFormModalIsOpen(true);
      form.setFieldsValue({
        id: Id,
        documentId: DocumentId,
        name: Name,
        lastName: LastName,
        gender: Gender,
        residentId: ResidentId,
        residentName: ResidentName,
        phone: Phone,
        scheduleGroupId: ScheduleGroupId,
        expirationDate: ExpirationDate ? dayjs(ExpirationDate) : null,
        status: Status,
        picture: Picture,
      });
      setSelectedPreAuthorizedId(Id);
      setPicture(Picture);

      setSelectedSchedules(
        schedulesData.schedules.map((schedule) => ({
          key: schedule.key,
          Id: schedule.Id,
          Description: schedule.Description,
          WeekDays: weekDaysNumbersToESText(schedule.WeekDays, true),
          TimeRange: `${schedule.StartTime} a ${schedule.EndTime}`,
        }))
      );
    } catch (err) {
      let message = "Servicio no disponible, intente más tarde.";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  };

  const onChangePicture = (info) => {
    const isJpgOrPng =
      info.file.type === "image/jpeg" || info.file.type === "image/png";
    if (!isJpgOrPng) {
      messageApi.error("¡Solo puede subir archivos JPG/PNG!");
      return;
    }

    const isLt2M = info.file.size / 1024 / 1024 < 2;

    if (!isLt2M) {
      messageApi.error("¡La imagen debe ser menor a 2MB!");
      return;
    }

    setFileList([info.file]);

    getBase64(info.file, (url) => {
      setPicture(url);
    });
  };

  const onBeforeUploadPicture = () => {
    return false;
  };

  const onRemovePicture = () => {
    setFileList([]);
    setPicture("");
  };

  const onClickDownloadButton = async () => {
    try {
      messageApi.open({ type: "loading", content: "Cargando..." });

      const response = await PreAuthorizedAPI.downloadPreAuthorized(token, {
        ...searchForm.getFieldsValue(),
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `Pre-autorizados ${moment().format("DD-MM-YYYY")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde.";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      messageApi.destroy();
    }
  };

  /**
   * Agrega el rango de horario seleccionado, a los que serán agregados al grupo.
   * @param {{key: number, Id: number, Description: string, WeekDays: string, TimeRange: string}} row - fila de información correspondiente a un rango horario
   */
  const addSelectedSchedule = (row) => {
    if (selectedSchedules.some((schedule) => schedule.Id === row.Id)) {
      messageApi.warning("El horario ya se encuentra en la lista.");
      return;
    }

    setSelectedSchedules([...selectedSchedules, row]);
    setScheduleModalIsOpen(false);
  };

  /**
   * Elimina rango de horario asociado al usuario.
   * @param {number} id - Identificador de rango de horario
   */
  const onRemoveSelectedSchedule = async (id) => {
    setSelectedSchedules(
      selectedSchedules.filter((schedule) => schedule.Id !== id)
    );

    try {
      const response = await SchedulesAPI.deleteSchedule(token, id);
      const responseBody = response.data;

      messageApi.open({
        type: responseBody.success ? "success" : "error",
        content: responseBody.message,
      });
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const resetScreen = () => {
    form.resetFields();
    searchForm.resetFields();
    setFormModalIsOpen(false);
    setIsSelected(false);
    setSelectedPreAuthorizedId(null);
    loadPreAuthorizedList();
    setFileList([]);
    setPicture("");
    setSelectedSchedules([]);
  };

  const resetModalForm = () => {
    form.resetFields();
    setIsSelected(false);
    setSelectedPreAuthorizedId(null);
    setFileList([]);
    setPicture("");
    setSelectedSchedules([]);
  };

  return (
    <div className="pre-authorized-maintenance">
      <>{messageContextHolder}</>
      <Modal
        forceRender
        centered
        open={formModalIsOpen}
        onCancel={() => setFormModalIsOpen(false)}
        width={900}
        onOk={() => {
          form.submit();
        }}
        footer={
          <FormButtonsWrap center>
            <Button icon={<ReloadOutlined />} onClick={() => resetModalForm()}>
              Limpiar
            </Button>
            <Button
              icon={<SaveFilled />}
              type="primary"
              onClick={() => form.submit()}
            >
              Guardar
            </Button>
          </FormButtonsWrap>
        }
      >
        <Form
          className="save-pre-authorized-form"
          initialValues={initialFormValues}
          form={form}
          onFinish={onFinishFormPreAuthorized}
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 12,
          }}
          layout="horizontal"
          autoComplete="off"
        >
          <h2 style={{ padding: "5px" }}>
            {isSelected ? "Editando" : "Crear Pre-autorizado"}
          </h2>
          <div className="custom-form-row">
            <div className="custom-form-col">
              <h3>Información Personal</h3>
              <Form.Item type="text" name="id" style={{ display: "none" }}>
                <Input />
              </Form.Item>
              <Form.Item label="No. Documento" name="documentId" required>
                <Input type="text" maxLength={20} required />
              </Form.Item>
              <Form.Item label="Nombre" name="name" required>
                <Input type="text" maxLength={64} required />
              </Form.Item>
              <Form.Item label="Apellido" name="lastName" required>
                <Input type="text" maxLength={64} required />
              </Form.Item>
              <Form.Item label="Género" name="gender" required>
                <Select>
                  <Select.Option value="">-- Seleccionar --</Select.Option>
                  <Select.Option value="M">Masculino</Select.Option>
                  <Select.Option value="F">Femenino</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Teléfono" name="phone">
                <Input
                  type="text"
                  maxLength={20}
                  onChange={(e) => {
                    form.setFieldValue(
                      "phone",
                      formatAsDOPhone(e.target.value)
                    );
                  }}
                  placeholder="XXX-XXX-XXXX"
                />
              </Form.Item>
            </div>
            <div className="custom-form-col">
              <h3>Información de Acceso</h3>
              <Form.Item label="Foto">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Upload
                    listType="picture-card"
                    style={{ display: "flex", flexDirection: "row" }}
                    fileList={fileList}
                    beforeUpload={onBeforeUploadPicture}
                    onChange={onChangePicture}
                    onRemove={onRemovePicture}
                  >
                    {fileList.length === 0 && <PlusOutlined />}
                  </Upload>

                  {picture && <Image src={picture} />}
                </div>
              </Form.Item>
              <Form.Item label="Residente" name="residentName" required>
                <AutoComplete
                  placeholder="Escriba para buscar"
                  options={residents}
                  onChange={onChangeResidentName}
                  onSelect={(id) => onSelectResidentName(id, form)}
                  allowClear
                  onClear={() => {
                    form.setFieldValue("residentId", "");
                    form.setFieldValue("residentName", "");
                  }}
                />
              </Form.Item>
              <Form.Item label="ID Residente" name="residentId" hidden>
                <Input type="text" maxLength={20} disabled />
              </Form.Item>
              <Form.Item label="Grupo Horario" name="scheduleGroupId">
                <Select>
                  <Select.Option value="">-- Seleccionar --</Select.Option>
                  {scheduleGroups.map((group) => (
                    <Select.Option key={group.Id} value={group.Id}>
                      {group.Name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Fecha Expiración" name="expirationDate">
                <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
              </Form.Item>
              <Form.Item label="Estado" name="status" required>
                <Select>
                  {Object.keys(statusMap).map((statusKey, index) => (
                    <Select.Option key={index} value={statusKey}>
                      {statusMap[statusKey]}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          <div>
            {selectedPreAuthorizedId !== null ? (
              <List
                style={{ marginBottom: "10px" }}
                header={
                  <div>
                    <b>Horarios del usuario</b>{" "}
                    <Button
                      icon={<PlusCircleFilled />}
                      size="small"
                      onClick={() => setScheduleModalIsOpen(true)}
                    >
                      Agregar
                    </Button>
                    {" - "}
                    <small>
                      Será válido cuando el usuario no tenga grupo de horario.
                    </small>
                  </div>
                }
                dataSource={selectedSchedules}
                bordered
                pagination={false}
                renderItem={(item) => (
                  <List.Item>
                    <div>
                      {item.Description} — ({item.WeekDays}) — {item.TimeRange}
                    </div>
                    <Button
                      type="link"
                      danger
                      onClick={() => onRemoveSelectedSchedule(item.Id)}
                    >
                      Remover
                    </Button>
                  </List.Item>
                )}
              />
            ) : (
              <Card
                style={{ display: "block", width: "300px", margin: "0 auto" }}
              >
                <ClockCircleOutlined /> Podrá agregar el horario específico
                cuando el pre-autorizado esté creado.
              </Card>
            )}
          </div>
        </Form>
      </Modal>
      <Modal
        width={800}
        title="Agrega el horario"
        open={scheduleModalIsOpen}
        onCancel={() => setScheduleModalIsOpen(false)}
        footer={null}
      >
        <ScheduleFrom onFinish={onFinishScheduleForm} />
      </Modal>
      <div className="pre-authorized-list-container">
        <h2>
          Pre-autorizados{" "}
          <Button onClick={() => setFormModalIsOpen(true)} type="primary">
            <UserAddOutlined /> Nuevo
          </Button>
        </h2>
        <Form
          initialValues={initialFindFormValues}
          form={searchForm}
          onFinish={() => loadPreAuthorizedList()}
          style={{ padding: "1em", display: "flex", gap: "10px" }}
          autoComplete="off"
        >
          <div>
            <Form.Item label="Documento" name="documentId">
              <Input />
            </Form.Item>
            <Form.Item label="Nombre Completo" name="fullName">
              <Input />
            </Form.Item>
          </div>
          <div>
            <Form.Item label="Género" name="gender" style={{ width: "220px" }}>
              <Select>
                <Select.Option value="">-- Seleccionar --</Select.Option>
                <Select.Option value="M">Masculino</Select.Option>
                <Select.Option value="F">Femenino</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Residente" name="residentName">
              <AutoComplete
                placeholder="Escriba para buscar"
                options={residents}
                onChange={onChangeResidentName}
                onSelect={(id) => onSelectResidentName(id, searchForm)}
                allowClear
                onClear={() => {
                  searchForm.setFieldValue("residentId", "");
                  searchForm.setFieldValue("residentName", "");
                }}
              />
            </Form.Item>
          </div>
          <div>
            <Form.Item hidden label="Resident Id" name="residentId">
              <Input />
            </Form.Item>
            <Form.Item
              label="Grupo Horario"
              name="scheduleGroupId"
              style={{ width: "300px" }}
            >
              <Select>
                <Select.Option value="">-- Seleccionar --</Select.Option>
                {scheduleGroups.map((group) => (
                  <Select.Option key={group.Id} value={group.Id}>
                    {group.Name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Creado Por" name="createdBy">
              <Input />
            </Form.Item>
          </div>
          <div>
            <Form.Item label="Estado" name="status" style={{ width: "210px" }}>
              <Select>
                <Select.Option value="">-- Seleccionar --</Select.Option>
                {Object.keys(statusMap).map((statusKey, index) => (
                  <Select.Option key={index} value={statusKey}>
                    {statusMap[statusKey]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <FormButtonsWrap>
              <Button type="primary" htmlType="submit">
                Buscar
              </Button>
              <Button
                htmlType="button"
                onClick={onClickDownloadButton}
                icon={<DownloadOutlined />}
              >
                Descargar
              </Button>
              <Button onClick={resetScreen}>Limpiar</Button>
            </FormButtonsWrap>
          </div>
        </Form>
        <DataTotalDisplay
          currentCount={preAuthorizedData.preAuthorized.length}
          totalCount={preAuthorizedData.totalCount}
        />
        <Table
          columns={listColumns}
          dataSource={preAuthorizedData.preAuthorized}
          size="small"
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: preAuthorizedData.totalCount,
          }}
          onChange={async (e) => {
            await loadPreAuthorizedList(e.current, e.pageSize);
            setCurrentPage(e.current);
            setCurrentPageSize(e.pageSize);
          }}
          scroll={{ y: "360px" }}
          style={{ height: "360px" }}
        />
      </div>
    </div>
  );
};

export default PreAuthorizedMaintenancePage;
